import { LitElement, html } from 'lit';
import styles from './studio-toggle.css.mjs';

export class StudioToggle extends LitElement {
  static styles = styles;

  static properties = {
    position: { type: String, reflect: true },
    state: { type: String, reflect: true },
    text: { type: String, reflect: true },
    checked: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
  };

  static validPositions = ['left', 'right'];
  static validStates = ['default', 'hover', 'focus', 'selected', 'disabled'];

  constructor() {
    super();
    this.position = 'left';
    this.state = 'default';
    this.text = '';
    this.checked = false;
    this.disabled = false;
    this._eventListeners = [];
  }

  set position(value) {
    this._position = StudioToggle.validPositions.includes(value) ? value : 'left';
  }

  get position() {
    return this._position;
  }

  set state(value) {
    this._state = StudioToggle.validStates.includes(value) ? value : 'default';
  }

  get state() {
    return this._state;
  }

  set text(value) {
    this._text = typeof value === 'string' ? value : '';
  }

  get text() {
    return this._text;
  }

  firstUpdated() {
    const wrapper = this.renderRoot.querySelector('.toggle-wrapper');
    const input = this.renderRoot.querySelector('input');
    if (!wrapper || !input) return;

    const setState = (value, add = true) => {
      if (!this.disabled && this.state !== 'selected') {
        this.state = add ? value : this.checked ? 'selected' : 'default';
      }
    };

    this._eventListeners = [
      {
        element: input,
        event: 'change',
        handler: (e) => {
          if (this.disabled) return;
          this.checked = e.target.checked;
          this.state = this.checked ? 'selected' : 'default';
          this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        },
      },
      { element: wrapper, event: 'mouseenter', handler: () => setState('hover') },
      { element: wrapper, event: 'mouseleave', handler: () => setState('hover', false) },
      { element: wrapper, event: 'focusin', handler: () => setState('focus') },
      { element: wrapper, event: 'focusout', handler: () => setState('focus', false) },
      { element: wrapper, event: 'mousedown', handler: () => setState('press') },
      { element: wrapper, event: 'mouseup', handler: () => setState('focus') },
      {
        element: wrapper,
        event: 'keydown',
        handler: (e) => {
          if (!this.disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            this.checked = !this.checked;
            this.state = this.checked ? 'selected' : 'default';
            this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
          }
        },
      },
    ];

    this._eventListeners.forEach(({ element, event, handler }) =>
      element.addEventListener(event, handler)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this._eventListeners = [];
  }

  render() {
    const isSelected = this.checked || this.state === 'selected';
    const isDisabled = this.disabled || this.state === 'disabled';

    return html`
      <label
        class="toggle-wrapper"
        role="switch"
        aria-checked="${isSelected}"
        aria-disabled="${isDisabled}"
        tabindex="${isDisabled ? -1 : 0}"
      >
        ${this.position === 'left'
          ? html`
              <slot name="label">
                ${this.text ? html`<span class="label">${this.text}</span>` : ''}
              </slot>
            `
          : ''}
        <input
          type="checkbox"
          ?checked="${isSelected}"
          ?disabled="${isDisabled}"
          aria-hidden="true"
        />
        <span class="toggle">
          <span class="knob"></span>
        </span>
        ${this.position === 'right'
          ? html`
              <slot name="label">
                ${this.text ? html`<span class="label">${this.text}</span>` : ''}
              </slot>
            `
          : ''}
        <slot></slot>
      </label>
    `;
  }
}

customElements.define('studio-toggle', StudioToggle);